import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { useSettingsDetailBackLink } from '@/utils/useMediaQuery';
import { formatPhone } from '@/utils/phone';
import { useCustomers } from '@/features/customers/hooks/useCustomers';
import type { Customer } from '@restaurant/api-client';
import {
  Block,
  BlockTitle,
  Link as F7Link,
  List,
  ListItem,
  Navbar,
  NavRight,
  Page,
  Preloader,
  Searchbar,
  Subnavbar,
} from 'framework7-react';
import { useEffect, useMemo, useState } from 'react';

function primaryPhone(c: Customer): string {
  const active = c.phoneNumbers.filter((p) => p.isActive);
  const primary = active.find((p) => p.isPrimary) ?? active[0];
  return primary?.phone ?? '-';
}

function displayName(c: Customer): string {
  return `${c.firstName} ${c.lastName}`.trim();
}

/** Section letter for the grouped list: first A–Z of the name, else "#". */
function groupKey(c: Customer): string {
  const first = displayName(c).charAt(0).toUpperCase();
  return first >= 'A' && first <= 'Z' ? first : '#';
}

/** Customers sorted by name and bucketed into alphabetical sections ("#" last). */
function groupCustomers(customers: Customer[]): [string, Customer[]][] {
  const buckets = new Map<string, Customer[]>();
  for (const c of [...customers].sort((a, b) =>
    displayName(a).localeCompare(displayName(b), undefined, { sensitivity: 'base' }),
  )) {
    const key = groupKey(c);
    (buckets.get(key) ?? buckets.set(key, []).get(key)!).push(c);
  }
  return [...buckets.entries()].sort(([a], [b]) =>
    a === '#' ? 1 : b === '#' ? -1 : a.localeCompare(b),
  );
}

export default function CustomersPage() {
  const t = useT();
  const tc = t.customers;
  // Reached from Settings via navigateMain (pushed onto the settings tab stack on
  // mobile) → a mobile back returns to Settings; desktop swaps the main view root
  // → undefined (no dead chevron). F7 hides it when Customers is a view root.
  const settingsBackLink = useSettingsDetailBackLink();
  const [raw, setRaw] = useState('');
  const [search, setSearch] = useState('');

  // Debounce keystrokes - the search hits the server, so don't refetch on
  // every character.
  useEffect(() => {
    const t = setTimeout(() => setSearch(raw.trim()), 300);
    return () => clearTimeout(t);
  }, [raw]);

  const { data, isLoading, isError } = useCustomers(search);
  const customers = useMemo(() => data?.items ?? [], [data]);
  const groups = useMemo(() => groupCustomers(customers), [customers]);

  return (
    <Page name="customers">
      <Navbar title={tc.title} backLink={settingsBackLink}>
        <NavRight>
          <F7Link iconF7="plus" href="/customers/create" />
        </NavRight>
        <Subnavbar inner={false}>
          <Searchbar
            customSearch
            backdrop={false}
            disableButton={false}
            placeholder={tc.searchPlaceholder}
            value={raw}
            onChange={(e) => setRaw((e.target as HTMLInputElement).value)}
            onClickClear={() => setRaw('')}
          />
        </Subnavbar>
      </Navbar>

      {isLoading && (
        <Block style={{ textAlign: 'center', marginTop: 40 }}>
          <Preloader />
        </Block>
      )}

      {!isLoading && isError && (
        <EmptyState
          icon="exclamationmark_triangle"
          title={tc.loadingError}
          description={tc.loadingErrorDescription}
        />
      )}

      {!isLoading && !isError && customers.length === 0 && (
        <EmptyState
          icon="person_2"
          title={search ? tc.noMatches : tc.empty}
          description={search ? tc.noMatchesDescription : tc.emptyDescription}
        />
      )}

      {!isLoading &&
        !isError &&
        customers.length > 0 &&
        groups.map(([letter, items], i) => (
          <div key={letter}>
            <BlockTitle medium style={{ marginTop: i === 0 ? 16 : 24 }}>
              {letter}
            </BlockTitle>
            <List strongIos strongMd dividersIos dividersMd insetIos insetMd>
              {items.map((c) => (
                <ListItem
                  key={c.id}
                  link={`/customers/${c.id}`}
                  title={displayName(c)}
                  after={formatPhone(primaryPhone(c))}
                  subtitle={c.email ?? undefined}
                  text={c.totalVisits > 0 ? tc.visitCount(c.totalVisits) : undefined}
                />
              ))}
            </List>
          </div>
        ))}
    </Page>
  );
}
